import { create } from 'zustand';
import type {
  Change,
  Line,
  Domain,
  Category,
  Block,
  PatrimoineLine,
  PatrimoineCategory,
  ResolvedBudget,
} from '@/engine/types';

export interface ResolvedPatrimoine {
  categories: PatrimoineCategory[];
  lines: PatrimoineLine[];
}

interface DraftState {
  isActive: boolean;
  entity: 'budget' | 'patrimoine' | null;
  changes: Map<string, Change>;
  isValidating: boolean;

  startDraft: (entity: 'budget' | 'patrimoine') => void;
  applyChange: (change: Change) => void;
  getChanges: () => Change[];
  hasChanges: () => boolean;
  // ED-02 : test-and-set SYNCHRONE — un double-clic sur Valider déclenchait
  // deux validations concurrentes (deux snapshots) car les deux appels
  // passaient les gardes avant le premier await. Retourne false si une
  // validation est déjà en cours.
  beginValidate: () => boolean;
  endValidate: () => void;
  reset: () => void;
}

export function buildChangeKey(change: Change): string {
  if (change.action === 'update' && change.field) {
    return `${change.entityType}:${change.entityId}:${change.field}`;
  }
  return `${change.entityType}:${change.entityId}`;
}

export function applyChangesToData(
  data: ResolvedBudget,
  changes: Change[],
): ResolvedBudget {
  let lines = [...data.lines];
  let domains = [...data.domains];
  let categories = [...data.categories];
  let blocks = [...data.blocks];

  for (const change of changes) {
    switch (change.entityType) {
      case 'line':
        if (change.action === 'create' && change.entity) {
          lines.push(change.entity as Line);
        } else if (change.action === 'delete') {
          lines = lines.filter(l => l.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          lines = lines.map(l =>
            l.id === change.entityId
              ? { ...l, [change.field!]: change.newValue }
              : l
          );
        }
        break;
      case 'domain':
        if (change.action === 'create' && change.entity) {
          domains.push(change.entity as Domain);
        } else if (change.action === 'delete') {
          domains = domains.filter(d => d.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          domains = domains.map(d =>
            d.id === change.entityId
              ? { ...d, [change.field!]: change.newValue }
              : d
          );
        }
        break;
      case 'category':
        if (change.action === 'create' && change.entity) {
          categories.push(change.entity as Category);
        } else if (change.action === 'delete') {
          categories = categories.filter(c => c.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          categories = categories.map(c =>
            c.id === change.entityId
              ? { ...c, [change.field!]: change.newValue }
              : c
          );
        }
        break;
      case 'block':
        if (change.action === 'create' && change.entity) {
          blocks.push(change.entity as Block);
        } else if (change.action === 'delete') {
          blocks = blocks.filter(b => b.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          blocks = blocks.map(b =>
            b.id === change.entityId
              ? { ...b, [change.field!]: change.newValue }
              : b
          );
        }
        break;
    }
  }

  return { blocks, domains, categories, lines };
}

export function applyPatrimoineChangesToData(
  data: ResolvedPatrimoine,
  changes: Change[],
): ResolvedPatrimoine {
  let lines = [...data.lines];
  let categories = [...data.categories];

  for (const change of changes) {
    switch (change.entityType) {
      case 'patrimoineLine':
        if (change.action === 'create' && change.entity) {
          lines.push(change.entity as PatrimoineLine);
        } else if (change.action === 'delete') {
          lines = lines.filter(l => l.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          lines = lines.map(l =>
            l.id === change.entityId
              ? { ...l, [change.field!]: change.newValue }
              : l
          );
        }
        break;
      case 'patrimoineCategory':
        if (change.action === 'create' && change.entity) {
          categories.push(change.entity as PatrimoineCategory);
        } else if (change.action === 'delete') {
          categories = categories.filter(c => c.id !== change.entityId);
        } else if (change.action === 'update' && change.field) {
          categories = categories.map(c =>
            c.id === change.entityId
              ? { ...c, [change.field!]: change.newValue }
              : c
          );
        }
        break;
    }
  }

  return { categories, lines };
}

export const useDraftStore = create<DraftState>((set, get) => ({
  isActive: false,
  entity: null,
  changes: new Map(),
  isValidating: false,

  startDraft: (entity) => set({
    isActive: true,
    entity,
    changes: new Map(),
    isValidating: false,
  }),

  beginValidate: () => {
    if (get().isValidating) return false;
    set({ isValidating: true });
    return true;
  },

  endValidate: () => set({ isValidating: false }),

  applyChange: (change) => {
    const { changes } = get();
    const newChanges = new Map(changes);
    const key = buildChangeKey(change);

    if (change.action === 'update') {
      const createKey = `${change.entityType}:${change.entityId}`;
      const existing = newChanges.get(createKey);
      if (existing && existing.action === 'create' && existing.entity) {
        const updatedEntity = { ...existing.entity, [change.field!]: change.newValue };
        newChanges.set(createKey, { ...existing, entity: updatedEntity });
        set({ changes: newChanges });
        return;
      }

      if (existing && existing.action === 'delete') {
        set({ changes: newChanges });
        return;
      }

      const existingUpdate = newChanges.get(key);
      if (existingUpdate && existingUpdate.action === 'update') {
        newChanges.set(key, {
          ...change,
          previousValue: existingUpdate.previousValue,
        });
      } else {
        newChanges.set(key, change);
      }
    } else if (change.action === 'delete') {
      const createKey = `${change.entityType}:${change.entityId}`;
      const existing = newChanges.get(createKey);
      if (existing && existing.action === 'create') {
        newChanges.delete(createKey);
        const prefix = `${change.entityType}:${change.entityId}:`;
        for (const k of Array.from(newChanges.keys())) {
          if (k.startsWith(prefix)) newChanges.delete(k);
        }
        set({ changes: newChanges });
        return;
      }

      const prefix = `${change.entityType}:${change.entityId}:`;
      for (const k of Array.from(newChanges.keys())) {
        if (k.startsWith(prefix)) newChanges.delete(k);
      }
      newChanges.set(key, change);
    } else if (change.action === 'create') {
      newChanges.set(key, change);
    }

    set({ changes: newChanges });
  },

  getChanges: () => Array.from(get().changes.values()),

  hasChanges: () => get().changes.size > 0,

  reset: () => set({
    isActive: false,
    entity: null,
    changes: new Map(),
    isValidating: false,
  }),
}));
